import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  isLscCfbDynastySetupEnabled,
  isLscCfbVisionScheduleIntakeEnabled,
  isLscCfbVodScheduleIntakeEnabled,
} from '../../config/featureFlags';
import {
  createCfbScheduleIntakeBatch,
  createCfbScheduleIntakeVodBatch,
  getCfbScheduleIntakeBatch,
  importCfbScheduleIntakeCandidates,
  LscApiError,
  updateCfbScheduleIntakeCandidate,
} from '../api';
import CfbScheduleIntakePipelineCard from '../components/CfbScheduleIntakePipelineCard';
import CfbMatchupChips from '../components/CfbMatchupChips';
import ErrorBanner from '../components/ErrorBanner';
import { intakeBatchPollingInProgress } from '../cfbScheduleIntakePolling';
import {
  candidateStatusLabel,
  confidenceTierLabel,
  coverageTierLabel,
  describeCandidateWarning,
  groupCandidatesByWeek,
  importableCandidateIds,
  validateIntakeFiles,
} from '../cfbScheduleVisionUtils';
import type {
  CfbVisionImportResult,
  CfbVisionIntakeBatchDetail,
  CfbVisionIntakeCandidate,
} from '../cfbScheduleVisionTypes';
import {
  buildVodIntakeRequestBody,
  validateVodIntakeForm,
  type CfbVodIntakeFormValues,
} from '../cfbScheduleVodUtils';
import { useCfbScheduleIntakeBatchPolling } from '../hooks/useCfbScheduleIntakeBatchPolling';
import { LscAuthGate } from '../hooks/useLscAuthGate';
import { useLeague } from '../hooks/useLeague';
import { lscPath, lscReturnTo } from '../routing';
import { useLscContentColumn } from '../lscContentColumn';

const CAPTURE_INSTRUCTIONS = [
  'Capture each relevant week.',
  'If using conference view, capture every conference represented in your league.',
  'Make sure the week number is visible.',
  'Avoid scrolling while taking the screenshot.',
  'Avoid highlighted rows covering team names.',
  'Upload all screenshots as one batch.',
  'You can review and edit before anything is added to your schedule.',
];

const VOD_INSTRUCTIONS = [
  'Paste a Twitch VOD archive URL (https://www.twitch.tv/videos/...).',
  'Scroll through your in-game schedule on camera so matchup rows stay visible.',
  'Optional start/end offsets trim the segment LSC analyzes.',
  'Extraction runs in the background — keep this page open to track progress.',
  'Review and edit candidates before adding draft games to your schedule.',
];

type IntakeTab = 'screenshot' | 'vod';

const EMPTY_VOD_FORM: CfbVodIntakeFormValues = {
  twitchVodUrl: '',
  startOffsetSec: '',
  endOffsetSec: '',
};

const LscCfbScheduleIntakePage: React.FC = () => {
  useLscContentColumn('narrow');
  const { leagueId } = useParams<{ leagueId: string }>();
  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/cfb/schedule/intake`)}>
      <CfbScheduleIntakeContent leagueId={leagueId ?? ''} />
    </LscAuthGate>
  );
};

interface EditForm {
  weekNumber: string;
  homeSchoolText: string;
  awaySchoolText: string;
}

function CfbScheduleIntakeContent({ leagueId }: { leagueId: string }) {
  const { league, isCommissioner } = useLeague();
  const screenshotEnabled = isLscCfbVisionScheduleIntakeEnabled();
  const vodEnabled = isLscCfbVodScheduleIntakeEnabled();
  const featureEnabled =
    isLscCfbDynastySetupEnabled() && (screenshotEnabled || vodEnabled);
  const canMutate = Boolean(isCommissioner);

  const [activeTab, setActiveTab] = useState<IntakeTab>(vodEnabled ? 'vod' : 'screenshot');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [vodForm, setVodForm] = useState<CfbVodIntakeFormValues>(EMPTY_VOD_FORM);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [submittingVod, setSubmittingVod] = useState(false);
  const [detail, setDetail] = useState<CfbVisionIntakeBatchDetail | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    weekNumber: '',
    homeSchoolText: '',
    awaySchoolText: '',
  });
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<CfbVisionImportResult | null>(null);

  const schedulePath = lscPath(`/leagues/${leagueId}/cfb/schedule`);

  const applyDetail = useCallback((next: CfbVisionIntakeBatchDetail) => {
    setDetail(next);
    setSelectedIds(new Set(importableCandidateIds(next.candidates)));
  }, []);

  const refreshBatch = useCallback(async () => {
    if (!detail) {
      return;
    }
    const refreshed = await getCfbScheduleIntakeBatch(leagueId, detail.batch.id);
    applyDetail(refreshed);
  }, [applyDetail, detail, leagueId]);

  const { polling, pollError } = useCfbScheduleIntakeBatchPolling({
    batch: detail?.batch ?? null,
    onRefresh: refreshBatch,
  });

  const pipelineInProgress = Boolean(detail && intakeBatchPollingInProgress(detail.batch));
  const showReview = Boolean(detail && !pipelineInProgress);

  const resetIntake = () => {
    setDetail(null);
    setFiles([]);
    setVodForm(EMPTY_VOD_FORM);
    setImportResult(null);
    setError(null);
    setEditingId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files ? Array.from(event.target.files) : [];
    setFiles(selected);
    setError(null);
  };

  const handleScan = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validateIntakeFiles(files);
    if (validationError) {
      setError(validationError);
      return;
    }
    setScanning(true);
    setError(null);
    setImportResult(null);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      const result = await createCfbScheduleIntakeBatch(leagueId, formData);
      applyDetail(result);
    } catch (err) {
      setError(
        err instanceof LscApiError ? err.message : 'Could not scan the screenshots.'
      );
    } finally {
      setScanning(false);
    }
  };

  const handleVodSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validateVodIntakeForm(vodForm);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmittingVod(true);
    setError(null);
    setImportResult(null);
    try {
      const result = await createCfbScheduleIntakeVodBatch(
        leagueId,
        buildVodIntakeRequestBody(vodForm)
      );
      applyDetail(result);
    } catch (err) {
      setError(
        err instanceof LscApiError ? err.message : 'Could not queue the Twitch VOD intake.'
      );
    } finally {
      setSubmittingVod(false);
    }
  };

  const toggleSelected = (candidateId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(candidateId)) {
        next.delete(candidateId);
      } else {
        next.add(candidateId);
      }
      return next;
    });
  };

  const beginEdit = (candidate: CfbVisionIntakeCandidate) => {
    setEditingId(candidate.id);
    setEditForm({
      weekNumber: candidate.week_number != null ? String(candidate.week_number) : '',
      homeSchoolText: candidate.home_school_text ?? '',
      awaySchoolText: candidate.away_school_text ?? '',
    });
  };

  const saveEdit = async (candidate: CfbVisionIntakeCandidate) => {
    if (!detail) return;
    setError(null);
    try {
      const week = editForm.weekNumber.trim() ? Number(editForm.weekNumber) : null;
      const { candidate: updated } = await updateCfbScheduleIntakeCandidate(
        leagueId,
        candidate.batch_id,
        candidate.id,
        {
          week_number: week,
          home_school_text: editForm.homeSchoolText.trim() || null,
          away_school_text: editForm.awaySchoolText.trim() || null,
        }
      );
      setDetail({
        ...detail,
        candidates: detail.candidates.map((c) => (c.id === updated.id ? updated : c)),
      });
      setEditingId(null);
    } catch (err) {
      setError(
        err instanceof LscApiError ? err.message : 'Could not update the candidate.'
      );
    }
  };

  const rejectCandidate = async (candidate: CfbVisionIntakeCandidate) => {
    if (!detail) return;
    setError(null);
    try {
      const { candidate: updated } = await updateCfbScheduleIntakeCandidate(
        leagueId,
        candidate.batch_id,
        candidate.id,
        { candidate_status: 'rejected' }
      );
      setDetail({
        ...detail,
        candidates: detail.candidates.map((c) => (c.id === updated.id ? updated : c)),
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(candidate.id);
        return next;
      });
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not ignore the candidate.');
    }
  };

  const handleImport = async () => {
    if (!detail) return;
    const ids = Array.from(selectedIds);
    if (ids.length === 0) {
      setError('Select at least one candidate to add.');
      return;
    }
    setImporting(true);
    setError(null);
    try {
      const result = await importCfbScheduleIntakeCandidates(leagueId, detail.batch.id, ids);
      setImportResult(result);
      const refreshed = await getCfbScheduleIntakeBatch(leagueId, detail.batch.id);
      applyDetail(refreshed);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not add the draft games.');
    } finally {
      setImporting(false);
    }
  };

  const weekGroups = useMemo(
    () => (detail ? groupCandidatesByWeek(detail.candidates) : []),
    [detail]
  );

  if (!featureEnabled || league?.game_key !== 'cfb') {
    return (
      <section className="card p-6" data-testid="cfb-intake-disabled">
        <h1 className="text-xl font-semibold text-white">Vision Schedule Intake</h1>
        <p className="mt-2 text-secondary-300 text-sm">
          Schedule intake is not enabled for this league.
        </p>
        <Link to={schedulePath} className="btn-secondary inline-block mt-4">
          Back to CFB Schedule
        </Link>
      </section>
    );
  }

  if (!canMutate) {
    return (
      <section className="card p-6" data-testid="cfb-intake-not-authorized">
        <h1 className="text-xl font-semibold text-white">Vision Schedule Intake</h1>
        <p className="mt-2 text-secondary-300 text-sm">
          Only league commissioners can import schedules.
        </p>
        <Link to={schedulePath} className="btn-secondary inline-block mt-4">
          Back to CFB Schedule
        </Link>
      </section>
    );
  }

  return (
    <div className="space-y-6" data-testid="cfb-intake-page">
      <section className="space-y-2">
        <h1 className="text-2xl font-semibold text-white">Vision Schedule Intake</h1>
        <p className="text-secondary-300 text-sm">
          Import your in-game schedule from screenshots or a Twitch VOD. LSC drafts candidates for
          your review before anything is added.
        </p>
        <Link to={schedulePath} className="text-primary-300 hover:text-primary-200 text-sm">
          Back to CFB Schedule
        </Link>
      </section>

      {screenshotEnabled && vodEnabled ? (
        <div className="flex gap-2" data-testid="cfb-intake-mode-tabs">
          <button
            type="button"
            className={activeTab === 'screenshot' ? 'btn-primary' : 'btn-secondary'}
            onClick={() => {
              setActiveTab('screenshot');
              setError(null);
            }}
            data-testid="cfb-intake-tab-screenshot"
          >
            Screenshots
          </button>
          <button
            type="button"
            className={activeTab === 'vod' ? 'btn-primary' : 'btn-secondary'}
            onClick={() => {
              setActiveTab('vod');
              setError(null);
            }}
            data-testid="cfb-intake-tab-vod"
          >
            Twitch VOD
          </button>
        </div>
      ) : null}

      {error ? (
        <div data-testid="cfb-intake-error">
          <ErrorBanner message={error} />
        </div>
      ) : null}

      {!detail ? (
        activeTab === 'vod' && vodEnabled ? (
          <>
            <section className="card p-6 space-y-3" data-testid="cfb-intake-vod-instructions">
              <h2 className="text-lg font-semibold text-white">Before you submit</h2>
              <ul className="list-disc pl-5 space-y-1 text-secondary-300 text-sm">
                {VOD_INSTRUCTIONS.map((instruction) => (
                  <li key={instruction}>{instruction}</li>
                ))}
              </ul>
            </section>

            <section className="card p-6 space-y-4" data-testid="cfb-intake-vod-form">
              <form className="space-y-4" onSubmit={(event) => void handleVodSubmit(event)}>
                <label className="block">
                  <span className="text-sm text-secondary-300">Twitch VOD URL</span>
                  <input
                    className="input-field mt-1 w-full"
                    type="url"
                    placeholder="https://www.twitch.tv/videos/2493990879"
                    value={vodForm.twitchVodUrl}
                    onChange={(event) =>
                      setVodForm((prev) => ({ ...prev, twitchVodUrl: event.target.value }))
                    }
                    data-testid="cfb-intake-vod-url-input"
                  />
                </label>
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-sm text-secondary-300">Start offset (seconds, optional)</span>
                    <input
                      className="input-field mt-1 w-full"
                      type="number"
                      min={0}
                      value={vodForm.startOffsetSec}
                      onChange={(event) =>
                        setVodForm((prev) => ({ ...prev, startOffsetSec: event.target.value }))
                      }
                      data-testid="cfb-intake-vod-start-offset"
                    />
                  </label>
                  <label className="block">
                    <span className="text-sm text-secondary-300">End offset (seconds, optional)</span>
                    <input
                      className="input-field mt-1 w-full"
                      type="number"
                      min={0}
                      value={vodForm.endOffsetSec}
                      onChange={(event) =>
                        setVodForm((prev) => ({ ...prev, endOffsetSec: event.target.value }))
                      }
                      data-testid="cfb-intake-vod-end-offset"
                    />
                  </label>
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submittingVod}
                  data-testid="cfb-intake-vod-submit-button"
                >
                  {submittingVod ? 'Queueing VOD extraction…' : 'Start VOD extraction'}
                </button>
              </form>
            </section>
          </>
        ) : screenshotEnabled ? (
          <>
            <section className="card p-6 space-y-3" data-testid="cfb-intake-capture-instructions">
              <h2 className="text-lg font-semibold text-white">Before you upload</h2>
              <ul className="list-disc pl-5 space-y-1 text-secondary-300 text-sm">
                {CAPTURE_INSTRUCTIONS.map((instruction) => (
                  <li key={instruction}>{instruction}</li>
                ))}
              </ul>
            </section>

            <section className="card p-6 space-y-4" data-testid="cfb-intake-upload">
              <form className="space-y-4" onSubmit={handleScan}>
                <label className="block">
                  <span className="text-sm text-secondary-300">
                    Screenshots (1–12 images: PNG, JPEG, or WebP)
                  </span>
                  <input
                    ref={fileInputRef}
                    className="input-field mt-1 w-full"
                    type="file"
                    multiple
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleFileChange}
                    data-testid="cfb-intake-file-input"
                  />
                </label>
                <p className="text-secondary-400 text-sm" data-testid="cfb-intake-selected-count">
                  {files.length === 0
                    ? 'No screenshots selected yet.'
                    : `${files.length} screenshot${files.length === 1 ? '' : 's'} selected.`}
                </p>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={scanning}
                  data-testid="cfb-intake-scan-button"
                >
                  {scanning ? 'Scanning schedule screenshots…' : 'Scan screenshots'}
                </button>
              </form>
            </section>
          </>
        ) : null
      ) : (
        <>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              className="btn-secondary"
              onClick={resetIntake}
              data-testid="cfb-intake-start-over"
            >
              Start over
            </button>
          </div>

          {pipelineInProgress && detail ? (
            <CfbScheduleIntakePipelineCard
              batch={detail.batch}
              polling={polling}
              pollError={pollError}
            />
          ) : null}

          {showReview && detail ? (
            <>
              <section className="card p-6 space-y-3" data-testid="cfb-intake-batch-summary">
                <div className="flex flex-wrap gap-4 text-sm text-secondary-300">
                  <span data-testid="cfb-intake-status">Status: {detail.batch.status}</span>
                  {detail.batch.intake_mode === 'twitch_vod' ? (
                    <span data-testid="cfb-intake-mode">Mode: Twitch VOD</span>
                  ) : (
                    <span>Images: {detail.batch.image_count}</span>
                  )}
                  <span>Candidates found: {detail.batch.candidate_count}</span>
                </div>
                <p className="text-white text-sm" data-testid="cfb-intake-coverage">
                  {coverageTierLabel(detail.batch.coverage_confidence)}
                </p>
                {detail.batch.coverage_warnings.length > 0 ? (
                  <ul
                    className="list-disc pl-5 space-y-1 text-amber-200 text-sm"
                    data-testid="cfb-intake-coverage-warnings"
                  >
                    {detail.batch.coverage_warnings.map((warning) => (
                      <li key={warning}>{warning}</li>
                    ))}
                  </ul>
                ) : null}
                {detail.batch.error_message ? (
                  <p className="text-red-300 text-sm">{detail.batch.error_message}</p>
                ) : null}
              </section>

              {importResult ? (
                <section className="card p-6 space-y-3" data-testid="cfb-intake-success">
                  <p className="text-white">
                    {importResult.created_count > 0
                      ? 'Selected candidates were added as draft schedule games.'
                      : 'No new draft games were added.'}
                  </p>
                  {importResult.skipped.length > 0 ? (
                    <ul className="list-disc pl-5 space-y-1 text-amber-200 text-sm">
                      {importResult.skipped.map((skip) => (
                        <li key={skip.candidate_id}>{skip.reason}</li>
                      ))}
                    </ul>
                  ) : null}
                  <Link
                    to={schedulePath}
                    className="btn-primary inline-block"
                    data-testid="cfb-intake-view-schedule"
                  >
                    View CFB Schedule
                  </Link>
                </section>
              ) : null}

              {detail.candidates.length === 0 ? (
                <section className="card p-6" data-testid="cfb-intake-empty">
                  <p className="text-secondary-300 text-sm">
                    No schedule rows were extracted. Adjust your capture and try again.
                  </p>
                </section>
              ) : (
                <section className="space-y-6" data-testid="cfb-intake-review">
                  {weekGroups.map((group) => (
                    <div key={group.weekLabel} className="card p-6 space-y-3">
                      <h2 className="text-lg font-semibold text-white">{group.weekLabel}</h2>
                      <div className="space-y-3">
                        {group.candidates.map((candidate) => (
                          <CandidateCard
                            key={candidate.id}
                            candidate={candidate}
                            selected={selectedIds.has(candidate.id)}
                            onToggle={() => toggleSelected(candidate.id)}
                            editing={editingId === candidate.id}
                            editForm={editForm}
                            onEditFormChange={setEditForm}
                            onBeginEdit={() => beginEdit(candidate)}
                            onCancelEdit={() => setEditingId(null)}
                            onSaveEdit={() => saveEdit(candidate)}
                            onReject={() => rejectCandidate(candidate)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}

                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={importing || selectedIds.size === 0}
                      onClick={() => void handleImport()}
                      data-testid="cfb-intake-import-button"
                    >
                      {importing ? 'Adding…' : 'Add selected as draft games'}
                    </button>
                    <span className="text-secondary-400 text-sm">
                      {selectedIds.size} selected
                    </span>
                  </div>
                </section>
              )}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

interface CandidateCardProps {
  candidate: CfbVisionIntakeCandidate;
  selected: boolean;
  onToggle: () => void;
  editing: boolean;
  editForm: EditForm;
  onEditFormChange: (form: EditForm) => void;
  onBeginEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onReject: () => void;
}

function slotTypeHint(slotType: string | null | undefined): string | null {
  if (slotType === 'cpu_placeholder') {
    return 'CPU opponent (auto-matched)';
  }
  return null;
}

function CandidateCard({
  candidate,
  selected,
  onToggle,
  editing,
  editForm,
  onEditFormChange,
  onBeginEdit,
  onCancelEdit,
  onSaveEdit,
  onReject,
}: CandidateCardProps) {
  const rejected = candidate.candidate_status === 'rejected';
  const imported = candidate.candidate_status === 'imported';
  const selectable = !rejected && !imported;
  const homeSlotHint = slotTypeHint(candidate.home_slot_type);
  const awaySlotHint = slotTypeHint(candidate.away_slot_type);
  const sourceLabel =
    candidate.source_type === 'vision_vod_twitch'
      ? 'Twitch VOD'
      : candidate.source_image_index != null
        ? `Screenshot ${candidate.source_image_index + 1}`
        : null;

  return (
    <div
      className="border border-secondary-800 rounded-lg p-4 space-y-2"
      data-testid={`cfb-intake-candidate-${candidate.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1"
            checked={selected}
            disabled={!selectable}
            onChange={onToggle}
            data-testid={`cfb-intake-candidate-select-${candidate.id}`}
          />
          <span className="text-white">
            <CfbMatchupChips
              awayLabel={candidate.away_school_text ?? 'Unknown'}
              homeLabel={candidate.home_school_text ?? 'Unknown'}
              awaySchoolId={candidate.away_school_id}
              homeSchoolId={candidate.home_school_id}
              showLabels
            />
          </span>
        </label>
        <div className="text-right text-xs space-y-1">
          <div className="text-secondary-300">{confidenceTierLabel(candidate.confidence_tier)}</div>
          <div
            className="text-secondary-400"
            data-testid={`cfb-intake-candidate-status-${candidate.id}`}
          >
            {candidateStatusLabel(candidate.candidate_status)}
          </div>
          {candidate.has_source_evidence ? (
            <div className="text-secondary-500">Evidence stored</div>
          ) : null}
        </div>
      </div>

      <div className="text-secondary-400 text-sm">
        {candidate.week_number != null ? `Week ${candidate.week_number}` : 'Week unknown'}
        {sourceLabel ? ` · ${sourceLabel}` : ''}
        {candidate.conference_name ? ` · ${candidate.conference_name}` : ''}
      </div>

      {homeSlotHint || awaySlotHint ? (
        <div className="text-secondary-500 text-xs space-y-1">
          {homeSlotHint ? <div>Home: {homeSlotHint}</div> : null}
          {awaySlotHint ? <div>Away: {awaySlotHint}</div> : null}
        </div>
      ) : null}

      {candidate.warnings && candidate.warnings.length > 0 ? (
        <ul className="list-disc pl-5 text-amber-200 text-xs space-y-1">
          {candidate.warnings.map((warning) => (
            <li key={warning}>{describeCandidateWarning(warning)}</li>
          ))}
        </ul>
      ) : null}

      {editing ? (
        <div className="grid gap-2 sm:grid-cols-3 pt-2" data-testid={`cfb-intake-edit-${candidate.id}`}>
          <label className="block">
            <span className="text-xs text-secondary-400">Week</span>
            <input
              className="input-field mt-1 w-full"
              type="number"
              min={1}
              value={editForm.weekNumber}
              onChange={(e) => onEditFormChange({ ...editForm, weekNumber: e.target.value })}
              data-testid={`cfb-intake-edit-week-${candidate.id}`}
            />
          </label>
          <label className="block">
            <span className="text-xs text-secondary-400">Home school</span>
            <input
              className="input-field mt-1 w-full"
              value={editForm.homeSchoolText}
              onChange={(e) => onEditFormChange({ ...editForm, homeSchoolText: e.target.value })}
              data-testid={`cfb-intake-edit-home-${candidate.id}`}
            />
          </label>
          <label className="block">
            <span className="text-xs text-secondary-400">Away school</span>
            <input
              className="input-field mt-1 w-full"
              value={editForm.awaySchoolText}
              onChange={(e) => onEditFormChange({ ...editForm, awaySchoolText: e.target.value })}
              data-testid={`cfb-intake-edit-away-${candidate.id}`}
            />
          </label>
          <div className="sm:col-span-3 flex gap-2">
            <button
              type="button"
              className="btn-primary"
              onClick={onSaveEdit}
              data-testid={`cfb-intake-edit-save-${candidate.id}`}
            >
              Save
            </button>
            <button type="button" className="btn-secondary" onClick={onCancelEdit}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-3 pt-1">
          {!imported ? (
            <button
              type="button"
              className="text-primary-300 hover:text-primary-200 text-sm"
              onClick={onBeginEdit}
              data-testid={`cfb-intake-candidate-edit-${candidate.id}`}
            >
              Edit
            </button>
          ) : null}
          {selectable ? (
            <button
              type="button"
              className="text-red-300 hover:text-red-200 text-sm"
              onClick={onReject}
              data-testid={`cfb-intake-candidate-reject-${candidate.id}`}
            >
              Ignore
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default LscCfbScheduleIntakePage;
